import readings from './damrakRows.json';
import type { BuildContext } from '../../types';
import type { PolygonIndex } from '../../../geo/polygonIndex';
import type { Lot, ReservedFootprint } from '../types';
import type { SurveyedRow } from './rowTypes';
import { damrakBlockReservations } from './damrakBlocks';
import { surveyedRowLots } from './rowLots';
import { fitDamrakWestPlots } from './damrakWestPlots';
import { claverblatYardReservations, fitClaverblatPlots } from './claverblatPlan';
import { fitDamrakBankLot, isDamrakWaterEnd } from './damrakBankLots';
import { fitOudebrugAlley } from './oudebrugAlleyPlan';

export const DAMRAK_ROWS = readings.rows as readonly SurveyedRow[];

/** Keep source conflicts reviewable; a missing entry is never replaced with an unnamed house. */
export function unresolvedDamrakReadings(lots: readonly Lot[]): readonly string[] {
  const built = new Set(lots.filter((l) => l.kind === 'house').map((l) => l.reconstruction?.id));
  return DAMRAK_ROWS.flatMap((r) => r.houses.filter((h) => !built.has(h.id)).map((h) => h.id));
}

/** Institutional reservations precede every ordinary house; audited blocks precede generic fill. */
export function planDamrakBlocks(
  ctx: Pick<BuildContext, 'layout' | 'site'>,
  buildable: PolygonIndex,
  reserved: readonly ReservedFootprint[]
): {
  readonly lots: readonly Lot[];
  readonly reserved: readonly ReservedFootprint[];
  readonly unresolved: readonly string[];
} {
  const blocks = damrakBlockReservations(ctx.layout);
  if (blocks.length === 0) return { lots: [], reserved: [], unresolved: [] };
  let lots = [
    ...fitClaverblatPlots(
      fitDamrakWestPlots(
        surveyedRowLots(DAMRAK_ROWS, ctx.layout, buildable, reserved),
        DAMRAK_ROWS,
        ctx.layout,
        buildable,
        reserved
      ),
      buildable,
      reserved
    ),
  ];
  // A rectangle can stop at an angled alley or a neighbour before reaching the bank.
  // Only an actual bank-ending body receives the water elevation; complete a connected
  // remainder when a dry warehouse fits within the original plot, never over the alley.
  for (const candidate of [...lots]) {
    if (
      candidate.reconstruction?.waterfront !== 'damrak' ||
      isDamrakWaterEnd(candidate, ctx.layout)
    )
      continue;
    const house =
      candidate.kind === 'house'
        ? candidate
        : lots.find(
            (lot) => lot.reconstruction?.id === candidate.reconstruction!.id.replace(/\/water$/, '')
          )!;
    const index = lots.indexOf(candidate);
    const reconstruction = { ...candidate.reconstruction };
    delete reconstruction.waterfront;
    lots[index] = { ...candidate, reconstruction };
    const fitted = fitDamrakBankLot({
      house,
      waterward: candidate.kind === 'backhouse' ? lots[index] : undefined,
      placed: lots,
      layout: ctx.layout,
      buildable,
      reserved,
    });
    if (fitted) {
      if (candidate.kind === 'backhouse') lots[index] = fitted;
      else lots.push(fitted);
    }
  }
  lots = [...fitOudebrugAlley(lots, ctx.layout, buildable, reserved)];
  const footprints: ReservedFootprint[] = lots.map((l) => ({
    id: `surveyed:${l.reconstruction!.id}`,
    polygon: { outer: [...l.corners], holes: [] },
  }));
  return {
    lots,
    reserved: [...blocks, ...footprints, ...claverblatYardReservations(lots)],
    unresolved: unresolvedDamrakReadings(lots),
  };
}
